<template>
  <section class="distribution-analytics">
    <header class="distribution-analytics__header">
      <div>
        <span class="distribution-analytics__eyebrow">Analitik pasien</span>
        <h2>Sebaran pasien per wilayah</h2>
        <p>Perbandingan jumlah pasien yang terdaftar pada setiap wilayah.</p>
      </div>
      <span class="distribution-analytics__period">
        <i class="far fa-calendar-check"></i>
        Data terkini
      </span>
    </header>

    <div class="distribution-analytics__summary">
      <div class="distribution-summary-item">
        <span class="distribution-summary-item__icon">
          <i class="fas fa-users"></i>
        </span>
        <div>
          <small>Total pasien</small>
          <strong>{{ formattedTotalPatients }}</strong>
        </div>
      </div>
      <div class="distribution-summary-item">
        <span class="distribution-summary-item__icon distribution-summary-item__icon--soft">
          <i class="fas fa-map-marker-alt"></i>
        </span>
        <div>
          <small>Wilayah tertinggi</small>
          <strong>{{ topRegion }}</strong>
        </div>
      </div>
    </div>

    <div class="distribution-analytics__chart">
      <apexchart
        type="bar"
        height="290"
        :options="chartOptions"
        :series="series"
      ></apexchart>
    </div>
  </section>
</template>

<script>
import module from "@/core/modules/CrudModule.js";

export default {
  name: "DistributionMapGraphic",

  data() {
    return {
      //   graph
      series: [
        {
          data: [0],
          name: "Jumlah Pasien",
        },
      ],
      chartOptions: {
        chart: {
          height: 290,
          type: "bar",
          fontFamily: "Poppins, Inter, sans-serif",
          foreColor: "#6B7280",
          toolbar: { show: false },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 500,
          },
        },
        colors: ["var(--skin-primary)", "#DCE5EF", "var(--skin-secondary)", "#C9D8E7"],
        plotOptions: {
          bar: {
            columnWidth: "48%",
            distributed: true,
            borderRadius: 7,
            borderRadiusApplication: "end",
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: "#E7EDF4",
          strokeDashArray: 4,
          padding: { left: 4, right: 8, top: 0, bottom: 0 },
        },
        states: {
          hover: { filter: { type: "darken", value: 0.06 } },
          active: { filter: { type: "none" } },
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: function (val) {
              return `${Number(val || 0).toLocaleString("id-ID")} pasien`;
            },
          },
        },
        xaxis: {
          categories: [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: "#778397",
              fontSize: "11px",
              fontWeight: 600,
            },
          },
        },
        yaxis: {
          labels: {
            show: true,
            formatter: function (val) {
              return Number(val || 0).toLocaleString("id-ID");
            },
            style: { colors: "#94A3B8", fontSize: "10px" },
          },
        },
      },
      // User Access
      btn: false,
    };
  },

  computed: {
    totalPatients() {
      const values = this.series[0] && Array.isArray(this.series[0].data)
        ? this.series[0].data
        : [];
      return values.reduce((total, value) => total + Number(value || 0), 0);
    },

    formattedTotalPatients() {
      return this.totalPatients.toLocaleString("id-ID");
    },

    topRegion() {
      const values = this.series[0] && Array.isArray(this.series[0].data)
        ? this.series[0].data
        : [];
      const categories = this.chartOptions.xaxis && this.chartOptions.xaxis.categories
        ? this.chartOptions.xaxis.categories
        : [];
      if (!values.length) return "Belum ada data";
      const highestIndex = values.reduce(
        (bestIndex, value, index, data) => Number(value || 0) > Number(data[bestIndex] || 0) ? index : bestIndex,
        0
      );
      return categories[highestIndex] || "Belum ada data";
    },
  },

  methods: {
    async get() {
      let data = await module.getDistributionGraphic(
        "patients/distribution-map/graphic"
      );

      if (data != null) {
        let series = data.series;
        this.series = [
          {
            name: "Jumlah Pasien",
            data: series,
          },
        ];


        this.chartOptions = {
          ...this.chartOptions,
          ...{
            xaxis: {
              categories: data.categories,
              labels: {
                style: {
                  colors: "#778397",
                  fontSize: "11px",
                  fontWeight: 600,
                },
              },
            },
          },
        };
      }
    },
  },

  mounted() {
    this.get();
  },
};
</script>

<style lang="scss">
.distribution-analytics {
  color: #1a2433;
}

.distribution-analytics__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}

.distribution-analytics__eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--skin-secondary);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.distribution-analytics__header h2 {
  margin: 0 0 5px;
  color: #1a2433 !important;
  font-size: 19px !important;
  font-weight: 700;
}

.distribution-analytics__header p {
  margin: 0;
  color: #6b7280;
  font-size: 12px;
}

.distribution-analytics__period {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 7px 11px;
  color: var(--skin-deep);
  border: 1px solid #e3eaf2;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.distribution-analytics__period i {
  color: var(--skin-secondary);
}

.distribution-analytics__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
}

.distribution-summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 170px;
  padding: 9px 11px;
  border: 1px solid #e7edf4;
  border-radius: 12px;
  background: #fbfcfe;
}

.distribution-summary-item__icon {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  place-items: center;
  color: #fff;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--skin-deep), var(--skin-primary), var(--skin-secondary));
}

.distribution-summary-item__icon--soft {
  color: var(--skin-secondary);
  background: rgba(var(--skin-secondary-rgb), 0.1);
}

.distribution-summary-item small,
.distribution-summary-item strong {
  display: block;
}

.distribution-summary-item small {
  margin-bottom: 2px;
  color: #7b8798 !important;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.distribution-summary-item strong {
  max-width: 190px;
  overflow: hidden;
  color: var(--skin-deep);
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.distribution-analytics__chart {
  min-width: 0;
}

.distribution-analytics .apexcharts-canvas,
.distribution-analytics .apexcharts-svg {
  background: transparent !important;
}

.distribution-analytics .apexcharts-tooltip {
  border: 0 !important;
  border-radius: 12px !important;
  box-shadow: 0 14px 34px rgba(var(--skin-deep-rgb), 0.16) !important;
}

@media (max-width: 575.98px) {
  .distribution-analytics__header {
    flex-direction: column;
  }

  .distribution-analytics__summary {
    display: grid;
    grid-template-columns: 1fr;
  }
}
</style>
